import type { InsurerStats } from 'BrokerTsLib/src/dtos'
import { getInsurerName } from 'BrokerTsLib/src/enums'
import { priceFormat } from 'BrokerTsLib/src/Utils/formatPrice'
import {
  MAX_COMPANIES_FOR_GROUPING, sortCompaniesByCount,
  sortCompaniesByTotalValue, type ChartDataItem
} from './types'

/**
 * Gets the label text for a company based on selected metric
 * Prices are formatted in the market currency (defaults to CZK when no code is given)
 */
export const getCompanyLabel = (
  company: ChartDataItem,
  selectedMetric: 'count' | 'totalValue',
  currencyCode?: string
) => selectedMetric === 'count' ? company.count.toString() : priceFormat(company.price, currencyCode)

/**
 * Creates a chart data item from company stats
 */
export const createChartDataItem = (
  company: InsurerStats,
  selectedMetric: 'count' | 'totalValue',
  insurers: Array<{ id: number; name: string }>
): ChartDataItem => ({
  name: insurers.find(x => x.id === company.insurerId)?.name ?? getInsurerName(company.insurerId),
  value: selectedMetric === 'count' ? company.count : company.price,
  count: company.count,
  price: company.price,
  insurerId: company.insurerId,
  fill: 'primary.500',
})

/**
 * Prepares chart data with smart grouping logic
 */
export const prepareChartData = (
  companies: InsurerStats[] | undefined,
  selectedMetric: 'count' | 'totalValue',
  insurers: Array<{ id: number; name: string }>
) => {
  const sortedCompanies = (selectedMetric === 'count'
    ? companies?.sort(sortCompaniesByCount)
    : companies?.sort(sortCompaniesByTotalValue)) || []

  const allChartData: ChartDataItem[] = sortedCompanies.map((company) =>
    createChartDataItem(company, selectedMetric, insurers)
  )

  // We do not want to force grouping when the group would contain one company
  // When MAX_COMPANIES_FOR_GROUPING === allChartData.length => no grouping needed
  // When MAX_COMPANIES_FOR_GROUPING + 1 === allChartData.length => no grouping needed

  const isGroupingNeeded = allChartData.length > MAX_COMPANIES_FOR_GROUPING + 1

  // Group companies
  const topCompanies = isGroupingNeeded ? allChartData.slice(0, Math.min(MAX_COMPANIES_FOR_GROUPING, allChartData.length)) : allChartData
  const otherCompanies = isGroupingNeeded ? allChartData.slice(Math.min(MAX_COMPANIES_FOR_GROUPING, allChartData.length)) : []

  // Create aggregated "Others" data
  const othersData: ChartDataItem | null = otherCompanies.length > 0 ? {
    name: 'Ostatní',
    value: otherCompanies.reduce((sum, company) => sum + company.value, 0),
    count: otherCompanies.reduce((sum, company) => sum + company.count, 0),
    price: otherCompanies.reduce((sum, company) => sum + company.price, 0),
    insurerId: -1,
    fill: 'gray.300',
    isOthers: true,
    companies: otherCompanies
  } : null

  return { allChartData, topCompanies, othersData } as const
}
